import React, { memo, useCallback } from 'react';
import { dir } from 'i18next';
import isEqual from 'lodash/isEqual';

import { Stack } from '@lyearn/ui';
import { LANGUAGE_I18N_MAP_WITH_LANG_ENUM } from '@/components/LanguageMenu/consts';
import UserSurveyQuestionEditor from '@/modules/corporate/modules/quiz/views/QuizEditor/components/QuestionEditor/components/UserSurveyQuestionEditor';
import { QuestionApplicableOnEnum, UpdateQuestionEntity } from '@/types/schema';

import QuestionsSettings from './components/QuestionsSettings';
import QuizQuestionEditor from './components/QuizQuestionEditor';
import ViewQuestionSettings from './components/ViewQuestionSettings';
import { QuestionEditorProps } from './types';

function QuestionEditor(props: QuestionEditorProps) {
  const {
    limitedEditingInfoState,
    viewMode,
    error,
    question,
    onUpdateQuestion,
    quizSkillIds,
    applicableOn,
    setNudgeState,
    accessType,
    initialQuestion,
    isLocalizedContent,
    languageType,
  } = props;

  const onChange = useCallback(
    (nextQuestion: UpdateQuestionEntity) => {
      if (!isEqual(question, nextQuestion)) {
        onUpdateQuestion(nextQuestion);
      }
    },
    [onUpdateQuestion, question],
  );

  const isApplicableOnQuiz = applicableOn === QuestionApplicableOnEnum.Quiz;

  return (
    <>
      <Stack
        alignItems="center"
        className="h-full flex-1 overflow-auto border-r border-divider"
        dir={languageType ? dir(LANGUAGE_I18N_MAP_WITH_LANG_ENUM[languageType]) : undefined}
        direction="col"
        lang={languageType ? LANGUAGE_I18N_MAP_WITH_LANG_ENUM[languageType] : undefined}>
        {isApplicableOnQuiz ? (
          <QuizQuestionEditor
            error={error}
            isLocalizedContent={isLocalizedContent}
            question={question}
            setNudgeState={setNudgeState}
            viewMode={viewMode}
            onChange={onChange}
          />
        ) : (
          <UserSurveyQuestionEditor
            error={error}
            question={question}
            viewMode={viewMode}
            onChange={onChange}
          />
        )}
      </Stack>
      {isApplicableOnQuiz && viewMode === 'view' ? (
        <ViewQuestionSettings accessType={accessType} question={question} />
      ) : (
        <QuestionsSettings
          accessType={accessType}
          applicableOn={applicableOn}
          error={error}
          initialQuestion={initialQuestion}
          limitedEditingInfoState={limitedEditingInfoState}
          question={question}
          quizSkillIds={quizSkillIds}
          viewMode={viewMode}
          onChange={onChange}
        />
      )}
    </>
  );
}

export default memo(QuestionEditor);
